import uuid from 'uuid-random';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useComponentConfig } from 'js/hooks/component';
import { Loader } from 'js/components/common/Loader';
import { useAppSelector } from 'js/store/hooks';
import { prop } from 'ramda';

import { useDynamicReducerDispatch, useDynamicReducerSelector } from '../../store';
import * as actions from '../../store/actions';
import * as server from '../../server';
import * as hooks from './hooks';

import style from './IFrame.scss';

export const IFrame = ({ src, onSuccess }) => {
    const form = useCallback((_form) => _form?.submit(), []);
    const iframe = useRef();
    const themeName = useAppSelector(prop(['themeName']));
    const retailCardNumber = useDynamicReducerSelector(prop('retailCardNumber'));
    const documentData = useDynamicReducerSelector(prop('documentData'));
    const playerData = useDynamicReducerSelector(prop('playerData'));
    const pin = useDynamicReducerSelector(prop('pin'));
    const fetchPlayerTags = hooks.useFetchPlayerTags();
    const fetchPlayerData = hooks.usePlayerData();
    const dynamicReducerDispatch = useDynamicReducerDispatch();
    const { system } = useComponentConfig();

    useEffect(() => {
        fetchPlayerTags();
        fetchPlayerData();
    }, [
        fetchPlayerTags,
        fetchPlayerData
    ]);

    const { action, token, name, returnUrl } = useMemo(() => {
        const url = new URL(src);

        return {
            name: `wallet-linking-${uuid()}`,
            token: url.searchParams.get('token'),
            action: `${url.origin}${url.pathname}`,
            returnUrl: `${window.location.origin}/${themeName}/js/app-out/payment.html`
        };
    }, [src, themeName]);

    const birthDate = useMemo(() => {
        if (!playerData) {
            return null;
        }

        return DateTime
            .fromFormat(playerData.birthDate, 'yyyy-MM-dd')
            .toFormat('MM/dd/yyyy');
    }, [playerData]);

    const identExpiry = useMemo(() => {
        if (!documentData) {
            return null;
        }

        return DateTime
            .fromISO(documentData.identExpiry)
            .toFormat('MM/yyyy');
    }, [documentData]);

    const handleMessage = useCallback(async ({ data, source }) => {
        if (source !== iframe.current.contentWindow) {
            return;
        }

        const { success } = data;

        if (success) {
            await server.linkWallet({
                system,
                pin
            });

            onSuccess();
        } else {
            dynamicReducerDispatch(actions.setOpenUrl(undefined));
        }
    }, [
        pin,
        system,
        onSuccess,
        dynamicReducerDispatch
    ]);

    useEffect(() => {
        window.addEventListener('message', handleMessage);

        return () => window.removeEventListener('message', handleMessage);
    }, [handleMessage]);

    if (!documentData || !playerData) {
        return <Loader />;
    }

    return <React.Fragment>
        <iframe
            name={name}
            className={style.iframe}
            ref={iframe}
        />
        <form action={action} target={name} method="post" ref={form}>
            <input type="hidden" name="IdentityToken" value={token} />
            <input type="hidden" name="ReturnURL" value={returnUrl} />
            <input type="hidden" name="AccountNumber" value={retailCardNumber} />
            <input type="hidden" name="FirstName" value={playerData.firstName} />
            <input type="hidden" name="LastName" value={playerData.lastName} />
            <input type="hidden" name="DateOfBirth" value={birthDate} />
            <input type="hidden" name="Email" value={playerData.email} />
            <input type="hidden" name="Address" value={playerData.address} />
            <input type="hidden" name="City" value={playerData.city} />
            <input type="hidden" name="State" value={playerData.state} />
            <input type="hidden" name="Country" value={playerData.countryCode} />
            <input type="hidden" name="PostalCode" value={playerData.zip} />
            <input type="hidden" name="IdentityType" value={documentData.identType} />
            <input type="hidden" name="IdentityNumber" value={documentData.identNumber} />
            <input type="hidden" name="IssuedByState" value={documentData.stateProvCode} />
            <input type="hidden" name="IssuedByCountry" value={playerData.countryCode} />
            <input type="hidden" name="ExpiryDate" value={identExpiry} />
        </form>
    </React.Fragment>;
};
